import React,{useState, useEffect, useCallback, useRef} from "react";
import atlasApi from "../utils/atlasapi";

/* Attributtabellen under kartet. Aapnes fra kontekstmenyen paa et vektorlag.

   Radene kommer fra /v3/uploads/<tabell>/features/, ikke fra flisene: flisene
   har bare det som er tegnet, og datasettene her gaar opp i 1,3 millioner
   rader. Telling, sortering og filtrering skjer derfor i Postgres, og tabellen
   henter én side av gangen mens brukeren ruller.

   Filtrene har to virkninger. De snevrer inn tabellen (serverside), og de
   settes samtidig som et Mapbox-filter paa laget slik at objektene som faller
   utenfor forsvinner fra kartet. Det siste gjoer forelderen — panelet sier bare
   fra hvilke betingelser som gjelder. */

const SIDE = 100;

/* Operatorene fra designet. '≠' er det velgeren viser; API-et tar imot begge. */
const OPS = ['=', '≠', '>', '<'];

const visVerdi = (v) => (v === null || typeof v === 'undefined' || v === '') ? '—' : String(v);

export default function LayerTablePanel({layer, projectId, onFilterChange, onClose}) {

	const [columns, setColumns]		= useState([]);
	const [rows, setRows]			= useState([]);
	const [total, setTotal]			= useState(null);
	const [filtered, setFiltered]	= useState(null);
	const [sort, setSort]			= useState(null);		//{col, dir}
	const [filters, setFilters]		= useState([]);			//[{field, op, value}]
	const [headMenu, setHeadMenu]	= useState(null);
	const [builder, setBuilder]		= useState(null);		//{field, op, value}
	const [laster, setLaster]		= useState(true);
	const [feil, setFeil]			= useState(null);

	const kropp		= useRef(null);
	const henter	= useRef(false);
	const markor	= useRef(null);

	const table = layer.table;

	/* Foerste side paa nytt hver gang laget, sorteringen eller filtrene endrer
	   seg. Radene vi alt har vist hoerer til den gamle spoerringen. */
	useEffect(() => {
		let levende = true;

		setLaster(true);
		setFeil(null);

		markor.current = null;

		atlasApi.getLayerFeatures(table, {limit: SIDE, offset: 0, orderBy: sort ? sort.col : null, dir: sort ? sort.dir : null, filters, projectId})
			.then((r) => {
				if(!levende) return;
				markor.current = r.cursor;
				setColumns(r.columns);
				setRows(r.rows);
				setTotal(r.total);
				setFiltered(r.filtered);
				setLaster(false);
				if(kropp.current) kropp.current.scrollTop = 0;
			})
			.catch((e) => {
				if(!levende) return;
				setFeil(e.message);
				setLaster(false);
			});

		return () => { levende = false; };
	}, [table, sort, filters, projectId]);

	/* Neste side naar brukeren naermer seg bunnen. henter-flagget hindrer at en
	   rask rulling ber om samme side flere ganger. */
	const onScroll = useCallback((e) => {
		const el = e.currentTarget;

		if(henter.current || laster) return;
		if(el.scrollHeight - el.scrollTop - el.clientHeight > 300) return;

		/* Uten sortering blar serveren med markoer, og null betyr at siste side
		   er lest. Med sortering gaar det paa offset, og da er antallet
		   treff fasiten — det staar fra foerste side og endrer seg ikke mens
		   sorteringen og filtrene ligger fast. */
		const mer = sort ? (filtered === null || rows.length < filtered) : markor.current !== null;

		if(!mer) return;

		henter.current = true;

		atlasApi.getLayerFeatures(table, {
			limit: SIDE,
			offset: rows.length,
			cursor: sort ? null : markor.current,
			counts: false,
			orderBy: sort ? sort.col : null,
			dir: sort ? sort.dir : null,
			filters,
			projectId
		})
			.then((r) => {
				markor.current = r.cursor;
				setRows((r0) => [...r0, ...r.rows]);
				henter.current = false;
			})
			.catch(() => { henter.current = false; });
	}, [table, rows.length, sort, filters, filtered, laster, projectId]);

	const settFiltre = (nye) => {
		setFilters(nye);
		if(onFilterChange) onFilterChange(nye, columns);
	};

	const fjernFilter = (i)	=> settFiltre(filters.filter((f, n) => n !== i));
	const nullstill			= () => settFiltre([]);

	const brukBuilder = () => {
		if(!builder || String(builder.value).trim() === '') return;
		settFiltre([...filters, {...builder, value: String(builder.value).trim()}]);
		setBuilder(null);
	};

	const gridStyle = {gridTemplateColumns: 'repeat(' + (columns.length || 1) + ', minmax(160px, 1fr))'};

	const sammendrag = total === null ? '' :
		(filters.length
			? filtered.toLocaleString() + ' of ' + total.toLocaleString() + ' features'
			: total.toLocaleString() + (total === 1 ? ' feature' : ' features'));

	return (
		<div className="layer-table-panel" onClick={(e) => e.stopPropagation()}>

			<div className="lt-head">
				<span className="lt-swatch" style={{background: layer.color}}></span>
				<span className="lt-name" title={layer.name}>{layer.name}</span>
				<span className="lt-summary">{sammendrag}</span>
				<span className="lt-close" onClick={onClose} title="Close">×</span>
			</div>

			<div className="lt-filters">
				<span className="lt-filters-label">Map filter:</span>

				{filters.map((f, i) => (
					<span className="lt-chip" key={i}>
						{f.field} {f.op} {f.value}
						<span className="lt-chip-x" onClick={() => fjernFilter(i)}>×</span>
					</span>
				))}

				<span className="lt-builder-wrp">
					<span className="lt-link" onClick={() => setBuilder(builder ? null : {field: columns.length ? columns[0].name : '', op: '=', value: ''})}>
						+ Add condition
					</span>

					{builder &&
						<div className="lt-builder">
							<div className="lt-builder-title">New condition</div>
							<div className="lt-builder-row">
								<select value={builder.field} onChange={(e) => setBuilder({...builder, field: e.target.value})}>
									{columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
								</select>
								<select value={builder.op} onChange={(e) => setBuilder({...builder, op: e.target.value})}>
									{OPS.map((o) => <option key={o} value={o}>{o}</option>)}
								</select>
							</div>
							<input
								value={builder.value}
								placeholder="Value…"
								onChange={(e) => setBuilder({...builder, value: e.target.value})}
								onKeyDown={(e) => { if(e.key === 'Enter') brukBuilder(); }}
							/>
							<div className="lt-builder-btns">
								<button className="lt-btn" onClick={() => setBuilder(null)}>Cancel</button>
								<button className="lt-btn primary" disabled={String(builder.value).trim() === ''} onClick={brukBuilder}>Apply</button>
							</div>
						</div>
					}
				</span>

				{!!filters.length &&
					<span className="lt-link lt-reset" onClick={nullstill}>Reset filter</span>
				}
			</div>

			<div className="lt-body" ref={kropp} onScroll={onScroll}>

				{feil && <div className="lt-message lt-error">{feil}</div>}

				{!feil && !!columns.length &&
					<>
						<div className="lt-row lt-head-row" style={gridStyle}>
							{columns.map((c) => {

								const aktiv = sort && sort.col === c.name;

								return (
									<div className={"lt-cell lt-th" + (aktiv ? " sorted" : "")} key={c.name} onClick={() => setHeadMenu(headMenu === c.name ? null : c.name)}>
										<span className="lt-th-label" title={c.name}>{c.name}</span>
										<span className="lt-th-arrow">{aktiv ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span>
										<i className="ri-arrow-down-s-line"></i>

										{headMenu === c.name &&
											<div className="lt-th-menu" onClick={(e) => e.stopPropagation()}>
												<div onClick={() => { setSort({col: c.name, dir: 'asc'}); setHeadMenu(null); }}><span>↓</span> Sort ascending</div>
												<div onClick={() => { setSort({col: c.name, dir: 'desc'}); setHeadMenu(null); }}><span>↑</span> Sort descending</div>
												{aktiv &&
													<div className="lt-th-clear" onClick={() => { setSort(null); setHeadMenu(null); }}><span>×</span> Clear sort</div>
												}
											</div>
										}
									</div>
								);
							})}
						</div>

						{rows.map((r, i) => (
							<div className="lt-row" style={gridStyle} key={i}>
								{r.map((v, n) => (
									<div className={"lt-cell" + (n === 0 ? " first" : "")} key={n} title={visVerdi(v)}>{visVerdi(v)}</div>
								))}
							</div>
						))}
					</>
				}

				{laster && <div className="lt-message">Loading…</div>}

				{!laster && !feil && !rows.length && <div className="lt-message">No features match the filter.</div>}
			</div>

			<div className="lt-foot">
				{filters.length
					? 'Filtered-out features are hidden on the map'
					: 'Showing ' + rows.length.toLocaleString() + ' of ' + (filtered === null ? '?' : filtered.toLocaleString())}
			</div>
		</div>
	);
}
